import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImagePlaceholder from './ImagePlaceholder';
import { useLanguage } from '../context/LanguageContext';

const FeatureSection = ({ title, description, imageLabel, imageUrl, imageCaptions, imageAlts, reverse, laundryCard, t }) => (
  <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center py-12`}>
    <div className="w-full lg:w-1/2">
      {laundryCard ? (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center shadow-sm">
          <div className="w-20 h-20 rounded-full bg-brand/10 flex items-center justify-center text-brand mb-6">
            <SafeIcon name="RefreshCw" className="w-10 h-10" />
          </div>
          <h4 className="text-2xl font-bold text-slate-900 mb-2">{title}</h4>
          <p className="text-slate-600">{description}</p>
        </div>
      ) : Array.isArray(imageUrl) && imageUrl.length === 2 ? (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <img src={imageUrl[0]} alt={imageAlts?.[0] || title} loading="lazy" className="w-full h-full object-cover aspect-square shadow-md rounded-2xl" />
            {imageCaptions?.[0] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[0]}</p>}
          </div>
          <div className="mt-8">
            <img src={imageUrl[1]} alt={imageAlts?.[1] || title} loading="lazy" className="w-full h-full object-cover aspect-square shadow-md rounded-2xl" />
            {imageCaptions?.[1] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[1]}</p>}
          </div>
        </div>
      ) : Array.isArray(imageUrl) && imageUrl.length === 3 ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <img src={imageUrl[0]} alt={imageAlts?.[0] || title} loading="lazy" className="w-full aspect-[4/3] object-cover shadow-md rounded-2xl" />
            {imageCaptions?.[0] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[0]}</p>}
          </div>
          <div>
            <img src={imageUrl[1]} alt={imageAlts?.[1] || title} loading="lazy" className="w-full aspect-square object-cover shadow-md rounded-2xl object-top" />
            {imageCaptions?.[1] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[1]}</p>}
          </div>
          <div>
            <img src={imageUrl[2]} alt={imageAlts?.[2] || title} loading="lazy" className="w-full aspect-square object-cover shadow-md rounded-2xl" />
            {imageCaptions?.[2] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[2]}</p>}
          </div>
        </div>
      ) : typeof imageUrl === 'string' ? (
        <div>
          <img src={imageUrl} alt={imageAlts?.[0] || title} loading="lazy" className="w-full aspect-[4/3] object-cover shadow-lg rounded-2xl" />
          {imageCaptions?.[0] && <p className="text-sm text-center text-slate-500 mt-2 font-medium">{imageCaptions[0]}</p>}
        </div>
      ) : (
        <ImagePlaceholder label={imageLabel} className="w-full aspect-[4/3] shadow-lg" />
      )}
    </div>
    <div className="w-full lg:w-1/2">
      <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{title}</h3>
      {typeof description === 'string' ? (
        <p className="text-lg text-slate-600 leading-relaxed">{description}</p>
      ) : (
        <div className="text-lg text-slate-600 leading-relaxed">{description}</div>
      )}
    </div>
  </div>
);

const Features = () => {
  const { t } = useLanguage();

  return (
    <section id="features" className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t.features.title}</h2>
          <p className="text-lg text-slate-600">{t.features.subtitle}</p>
        </div>
        
        <div className="space-y-8 lg:space-y-0 divide-y divide-slate-100 lg:divide-y-0">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              title={t.features.f1.title} 
              description={t.features.f1.desc} 
              imageUrl="https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/75d75d5bcdcbdebc242e136eefb18dbe.png" 
              imageAlts={[t.gallery.alts[3]]} 
              t={t}
            />
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              reverse 
              title={t.features.f2.title} 
              description={t.features.f2.desc} 
              imageUrl="https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/4a6ea99184e24cad8b73065a5304879b.png" 
              imageAlts={[t.gallery.alts[1]]}
              t={t} 
            />
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              title={t.features.f3.title} 
              description={t.features.f3.desc} 
              laundryCard={true}
              t={t} 
            />
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              reverse 
              title={t.features.f4.title} 
              description={
                <div className="space-y-6">
                  <p>{t.features.f4.desc}</p>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">{t.features.f4.sinkHeading}</h4>
                    <p>{t.features.f4.sinkDesc}</p>
                  </div>
                </div>
              } 
              imageUrl={[
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/c2da0c49fb562f2f3d86abaf5b52c786.png",
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/c926c099811db65f3f8aa999fdbe5a21.png",
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/584bd13dee4b82205c740e6b653e3785.png"
              ]} 
              imageAlts={[t.gallery.alts[4], t.gallery.alts[5], t.gallery.alts[6]]}
              t={t} 
            />
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              title={t.features.f5.title} 
              description={
                <div className="space-y-4">
                  <p><strong className="font-semibold text-slate-800">{t.features.f5.cap1}:</strong> {t.features.f5.p1}</p>
                  <p><strong className="font-semibold text-slate-800">{t.features.f5.cap2}:</strong> {t.features.f5.p2}</p>
                  <p className="text-sm text-slate-500 italic mt-4">{t.features.f5.note}</p>
                </div>
              } 
              imageUrl={[
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/0b8ef8635f21bd8ad88762f09aecc748.png",
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvanBlZw==/873ba2c2bcf3318500e629e3ed7f7625.jpg"
              ]} 
              imageCaptions={[t.features.f5.cap1, t.features.f5.cap2]} 
              imageAlts={[t.gallery.alts[7], t.gallery.alts[9]]}
              t={t} 
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="py-12 lg:py-16">
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="text-brand font-bold tracking-wider uppercase text-sm mb-3 block">{t.features.view.label}</span>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">{t.features.view.title}</h3>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                  {t.features.view.desc}
                </p>
              </div>
              <div className="w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl relative">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/ec73c53a01da29aa638dd0d79f80dbff.png"
                  alt={t.gallery.alts[8]}
                  loading="lazy"
                  className="w-full aspect-[4/3] md:aspect-[21/9] object-cover object-center brightness-105 contrast-105 saturate-105"
                />
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              reverse 
              title={t.features.f6.title} 
              description={t.features.f6.desc} 
              imageUrl={[
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/1034b4bd631646c59e473b22601bac1b.png",
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/1da577f87b7d749eed8863bbb04a255b.png",
                "https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/22856906de6fd1ca70b4d6880157e420.png"
              ]} 
              imageCaptions={[t.features.f6.cap1, t.features.f6.cap2, t.features.f6.cap3]} 
              imageAlts={[t.gallery.alts[10], t.gallery.alts[11], t.gallery.alts[12]]}
              t={t} 
            />
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <FeatureSection 
              title={t.features.f7.title} 
              description={t.features.f7.desc} 
              imageUrl="https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2UvcG5n/5225213fd318172af975e2db9796b0a8.png" 
              imageAlts={[t.gallery.alts[13]]}
              t={t} 
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Features;